import { Socket } from 'socket.io-client';
import {
  WorkflowRunStatus,
  WorkflowRunStep,
  WorkflowRunStepState,
  WorkflowStepError,
  WorkflowStepKind,
} from '@digital-twins/shared-types';

import { connectWebSocket } from './websocket';

/**
 * Workflow run live transport (Assistant Platform v1 Phase 2, Step B).
 *
 * The gateway half is `ProgressGateway.handleWorkflowRunSubscribe`
 * (`apps/backend/src/progress/progress.gateway.ts`), which declares NO
 * namespace — i.e. the default one. `chat-socket.ts` is therefore the wrong
 * donor: it connects to `/chat`, where `subscribe-workflow-run` has no handler,
 * and a client copied from it emits into silence with no error to show for it.
 * The right donor is the quick-study block in `./websocket.ts`, which talks to
 * this same gateway with the same room-per-token shape.
 *
 * This module never calls `io()`. socket.io-client v4 multiplexes by URL, so a
 * second `io()` for the same origin hands back the very Socket instance
 * `websocket.ts` already holds — two modules would then both own its lifecycle
 * and either could tear the other down. Everything here goes through
 * `connectWebSocket()`.
 */

/**
 * The DESIRED subscription set, mirroring `desiredQuickStudyRunSubscriptions`
 * in `./websocket.ts`. It deliberately survives a disconnect: a mid-run socket
 * blip must re-join the run room without the pane doing anything.
 */
const desiredWorkflowRunSubscriptions = new Set<string>();

/**
 * The socket instance our reconnect listener is currently attached to.
 *
 * `websocket.ts`'s own `connect` handler re-emits ONLY the quick-study and
 * questionnaire-draft subscriptions, so without a listener of our own a network
 * blip would permanently silence the run pane. Attaching it here rather than
 * editing `websocket.ts` is what avoids the import cycle
 * (`websocket -> workflow-socket -> websocket`).
 *
 * Tracked per socket INSTANCE rather than with a bare boolean because
 * `disconnectWebSocket()` nulls the singleton, so the next `connectWebSocket()`
 * builds a fresh Socket that our old listener is not attached to. Comparing
 * identity registers the handler exactly once per instance, which is the
 * "exactly once" that actually matters: repeated subscribes never stack
 * duplicate handlers.
 */
let reconnectListenerSocket: Socket | null = null;

function ensureWorkflowRunReconnectListener(ws: Socket): void {
  if (reconnectListenerSocket === ws) {
    return;
  }
  reconnectListenerSocket = ws;
  ws.on('connect', () => {
    for (const runToken of desiredWorkflowRunSubscriptions) {
      ws.emit('subscribe-workflow-run', { runToken });
    }
  });
}

/** The shared socket, with our reconnect re-join guaranteed to be attached. */
function workflowRunSocket(): Socket {
  const ws = connectWebSocket();
  ensureWorkflowRunReconnectListener(ws);
  return ws;
}

/**
 * `workflow-run:{token}:sync`, emitted unconditionally inside the subscribe
 * handler and never afterwards.
 *
 * It is the run's CURRENT state with the full step list, so treat it as an
 * authoritative FULL REPLACE and never a merge: that makes a duplicate frame on
 * reconnect idempotent by construction. It doubles as terminal replay — a run
 * that finished while the socket was away had its live terminal event delivered
 * to an empty room, and this frame carrying `isTerminal` is what stops the pane
 * spinning forever.
 *
 * There is deliberately no sequence-number protocol anywhere in this gateway.
 * Socket.IO guarantees per-connection ordering and this frame is emitted
 * synchronously inside the subscribe handler, so it always precedes later
 * broadcasts. A sequence buffer here would be dead code.
 */
export interface WorkflowRunSyncPayload {
  runToken: string;
  status: WorkflowRunStatus;
  currentStepIndex: number;
  steps: Array<WorkflowRunStep>;
  error: WorkflowStepError | null;
  isTerminal: boolean;
  timestamp: string;
}

/**
 * `workflow-run:{token}:status`, the run-level event fanned out verbatim from
 * `WorkflowRunStatusEventPayload` (`workflow-run.events.ts`).
 *
 * Thin on purpose: it carries no `steps`, so a consumer that needs the step
 * list after a status change must read it back over REST.
 */
export interface WorkflowRunStatusPayload {
  runToken: string;
  status: WorkflowRunStatus;
  currentStepIndex: number;
  /** Present only on a `failed` status. */
  errorCode?: string;
  errorMessage?: string;
  timestamp: string;
}

/**
 * `workflow-run:{token}:step`, the step-level event fanned out verbatim from
 * `WorkflowRunStepEventPayload` (`workflow-run.events.ts`).
 *
 * It carries NO `output`, no `outputOverride`, no `error` and no `isRetryable`.
 * It is a fast optimistic hint and REST is the truth: every terminal state and
 * every `awaiting_approval` must trigger a `workflowRunsApi.get(runToken)`
 * readback. That is not an optimization — without it a gate card renders zero
 * artifacts and Retry never appears.
 */
export interface WorkflowRunStepPayload {
  runToken: string;
  stepId: string;
  stepIndex: number;
  stepKey: string;
  stepKind: WorkflowStepKind;
  state: WorkflowRunStepState;
  attempt: number;
  timestamp: string;
}

/**
 * Join a workflow run room. The subscription survives reconnects until
 * `unsubscribeFromWorkflowRun` drops it.
 *
 * The gateway re-derives ownership through `WorkflowRunService.getRun` and
 * silently declines a run outside the caller's organization, so a denied join
 * looks exactly like a run that never emits. The REST readback is what
 * distinguishes the two.
 */
export function subscribeToWorkflowRun(runToken: string): void {
  desiredWorkflowRunSubscriptions.add(runToken);
  const ws = workflowRunSocket();
  ws.emit('subscribe-workflow-run', { runToken });
}

/** Re-emit the join for an already-desired run (mirrors `resubscribeToQuickStudyRun`). */
export function resubscribeToWorkflowRun(runToken: string): void {
  if (!desiredWorkflowRunSubscriptions.has(runToken)) {
    return;
  }
  const ws = workflowRunSocket();
  ws.emit('subscribe-workflow-run', { runToken });
}

export function unsubscribeFromWorkflowRun(runToken: string): void {
  desiredWorkflowRunSubscriptions.delete(runToken);
  const ws = workflowRunSocket();
  if (ws.connected) {
    ws.emit('unsubscribe-workflow-run', { runToken });
  }
}

/**
 * Drop every desired workflow-run subscription.
 *
 * `disconnectWebSocket()` clears the quick-study and questionnaire-draft sets
 * but cannot clear this one: it lives in a module `websocket.ts` must not
 * import. A logout path that calls `disconnectWebSocket()` must call this too,
 * or the next session's fresh socket re-joins the previous user's run rooms on
 * connect.
 */
export function resetWorkflowRunSubscriptions(): void {
  desiredWorkflowRunSubscriptions.clear();
}

/** Test/diagnostic read of the desired set. Never mutate the returned array. */
export function getDesiredWorkflowRunSubscriptions(): Array<string> {
  return Array.from(desiredWorkflowRunSubscriptions);
}

export function onWorkflowRunSync(
  runToken: string,
  callback: (payload: WorkflowRunSyncPayload) => void,
): () => void {
  const ws = workflowRunSocket();
  const event = `workflow-run:${runToken}:sync`;
  ws.on(event, callback);
  return () => ws.off(event, callback);
}

export function onWorkflowRunStatus(
  runToken: string,
  callback: (payload: WorkflowRunStatusPayload) => void,
): () => void {
  const ws = workflowRunSocket();
  const event = `workflow-run:${runToken}:status`;
  ws.on(event, callback);
  return () => ws.off(event, callback);
}

export function onWorkflowRunStep(
  runToken: string,
  callback: (payload: WorkflowRunStepPayload) => void,
): () => void {
  const ws = workflowRunSocket();
  const event = `workflow-run:${runToken}:step`;
  ws.on(event, callback);
  return () => ws.off(event, callback);
}
